import { GroupMenu, ItemProps } from "@/components/sidebar/sidebar-utils";
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { useAirport } from "@/stores/AirportContext";
import { useSessionData } from "@/stores/SessionDataContext";
import {
  BookUser,
  Earth,
  PlaneTakeoff,
  TicketsPlane,
  TowerControl,
} from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const DATA_PREFIX = "/data";

function AirportSection() {
  const { hasCustoms, hasBaggage, hasAPM } = useAirport();
  const sessionData = useSessionData();
  const URL_PREFIX = `${DATA_PREFIX}/airport`;
  const items: ItemProps[] = [
    {
      url: `/`,
      title: "Airport Overview",
      icon: TowerControl,
    },
    {
      url: `${URL_PREFIX}/pax`,
      title: "Passenger Overview",
      icon: BookUser,
    },
    {
      title: "Terminal Overview",
      url: `${URL_PREFIX}/terminals`,
      icon: TicketsPlane,
    },
  ];

  if (hasCustoms) {
    items.push({
      title: "Customs Overview",
      url: `${URL_PREFIX}/intl`,
      icon: Earth,
    });
  }

  if (hasBaggage) {
    items.push({
      title: "Baggage Overview",
      url: `${URL_PREFIX}/baggage`,
      icon: Earth,
    });
  }

  if (hasAPM) {
    const apmPrefix = sessionData.airport.apm?.custom_prefix || "APM";
    items.push({
      title: `${apmPrefix} Overview`,
      url: `${URL_PREFIX}/apm`,
      icon: PlaneTakeoff,
    });
  }

  return <GroupMenu title={"Airport"} items={items} defaultOpen={true} />;
}

function TerminalSection() {
  const { terminals, code_iata } = useSessionData();
  const items: ItemProps[] = terminals.map((t) => {
    const URL_PREFIX = `${DATA_PREFIX}/terminal/${t.terminal_name}`;

    // Handle special terminal name cases
    let terminalTitle = "";
    if (t.terminal_name === "Jeppesen") {
      terminalTitle = t.terminal_name + " Terminal";
    } else if (t.terminal_name === "International") {
      terminalTitle = "International Terminal";
    } else if (t.terminal_name === "Main") {
      terminalTitle = "Main Terminal";
    } else {
      terminalTitle = "Terminal " + t.terminal_name;
    }

    // Check if we should hide restrooms and gate lounge for DEN/Jeppesen terminal in production
    const isProduction = import.meta.env.MODE === "production";
    const isDenAirport = code_iata === "DEN";
    const isJeppesenTerminal = t.terminal_name === "Jeppesen";
    const shouldHideItems = isProduction && isDenAirport && isJeppesenTerminal;

    const allSubItems = [
      {
        url: `${URL_PREFIX}/flow`,
        title: "Passenger Flow",
      },
      {
        url: `${URL_PREFIX}/pain-points`,
        title: "Pain Points",
      },
      {
        url: `${URL_PREFIX}/doors`,
        title: "Curb",
      },
      {
        url: `${URL_PREFIX}/ticketing`,
        title: "Ticketing",
      },
      {
        url: `${URL_PREFIX}/checkpoints`,
        title: "Checkpoints",
      },
      {
        url: `${URL_PREFIX}/restrooms`,
        title: "Restrooms",
      },
      {
        url: `${URL_PREFIX}/gates`,
        title: "Gate Lounge",
      },
    ];

    // Filter out restrooms and gate lounge if conditions are met
    const subItems = shouldHideItems
      ? allSubItems.filter(
          (item) => item.title !== "Restrooms" && item.title !== "Gate Lounge"
        )
      : allSubItems;

    return {
      subItems,
      title: terminalTitle,
      icon: PlaneTakeoff,
    };
  });
  return <GroupMenu title={"Terminals"} items={items} />;
}

function LandingFeeSection() {
  const { airport } = useSessionData();
  if (!airport.features.landing_fees) {
    return null;
  }
  const URL_PREFIX = `${DATA_PREFIX}/landing`;

  // Helper to format date as YYYY-MM-DD
  const formatDate = (date: Date) => {
    return date.toISOString().split("T")[0];
  };

  // Get yesterday's date at UTC noon
  const today = new Date();

  const items: ItemProps[] = [
    {
      url: `${URL_PREFIX}/day/${formatDate(today)}`,
      title: "Landing Fees",
      icon: TowerControl,
    },
    {
      url: `${URL_PREFIX}/report`,
      title: "Reports",
      icon: BookUser,
    },
  ];

  return <GroupMenu title={"Landing Fees"} items={items} defaultOpen={false} />;
}

function ConcourseSection() {
  const { concourses, airport_config } = useSessionData();
  if (!airport_config.has_concourse) {
    return null;
  }

  const items: ItemProps[] = concourses.map((t) => {
    const URL_PREFIX = `${DATA_PREFIX}/concourse/${t.name}`;
    return {
      subItems: [
        {
          url: `${URL_PREFIX}/flow`,
          title: "Passenger Flow",
        },
        {
          url: `${URL_PREFIX}/pain-points`,
          title: "Pain Points",
        },
        {
          url: `${URL_PREFIX}/restrooms`,
          title: "Restrooms",
        },
        {
          url: `${URL_PREFIX}/gates`,
          title: "Gate Lounge",
        },
      ],
      title: "Concourse " + t.name,
    };
  });
  return <GroupMenu title={"Concourses"} items={items} />;
}

function ExploreSection() {
  const { aiChatEnabled } = useAirport();

  const items: ItemProps[] = [];

  if (aiChatEnabled) {
    items.push({
      url: `/chat`,
      title: "Intelligence",
    });
  }

  items.push(
    {
      url: `/forecast`,
      title: "Forecasts",
    },
    {
      url: `/plays`,
      title: "Plays",
    }
  );

  items.push(
    {
      url: `/explore`,
      title: "Explore Data",
    },
    {
      url: `/flights`,
      title: "Flights",
    }
  );

  return <GroupMenu title={"Data"} items={items} defaultOpen={true} />;
}

function ConfigurationSection() {
  const { is_admin } = useSessionData();

  if (!is_admin) {
    return null;
  }
  const items: ItemProps[] = [
    {
      url: `/setup/airport`,
      title: "Manage Airport",
      icon: TowerControl,
    },
  ];
  return <GroupMenu title={"Configuration"} items={items} defaultOpen={true} />;
}

export function AppSidebar() {
  const { code_iata } = useSessionData();
  return (
    <Sidebar>
      <SidebarHeader>
        <Link to={"/"}>
          <div className="mt-2 flex items-center cursor-pointer">
            <div className="text-2xl font-bold">RaidoHub</div>
            <div className="ml-3 mt-0.5 bg-gradient-to-r from-indigo-600 to-indigo-500 text-white text-xs px-2 py-1 rounded-md shadow-sm shadow-indigo-900/30 font-semibold tracking-wide">
              {code_iata}
            </div>
          </div>
        </Link>
      </SidebarHeader>
      <SidebarContent>
        <AirportSection />
        <LandingFeeSection />
        <TerminalSection />
        <ConcourseSection />
        <ExploreSection />
        <ConfigurationSection />
      </SidebarContent>
    </Sidebar>
  );
}

function RetailOverviewSection() {
  const { marketDemandEnabled } = useAirport();
  const navigate = useNavigate();
  const location = useLocation();

  // Overview and Market Demand both carry the selected store in the URL, as
  // "/store/:id" and "/market-demand/store/:id". Read it from whichever page
  // we are on so switching between them keeps the same store selected.
  const currentStore =
    location.pathname.match(/^(?:\/market-demand)?\/store\/(\d+)/)?.[1] ??
    null;

  // Translate any retail path to its Overview equivalent, dropping the
  // "/market-demand" prefix but keeping the store.
  const toOverviewPath = (path: string) => {
    if (!path.startsWith("/market-demand")) return path;
    const store = path.match(/^\/market-demand\/store\/(\d+)/)?.[1];
    return store ? `/store/${store}` : "/";
  };

  // When the user clicks "Overview" after visiting Intelligence (/chat),
  // restore them to the store/page they were on before they navigated away.
  // We read the path saved by RetailLayout into sessionStorage["ai.lastContext"].
  // Only restore retail-scoped paths (starts with / but not /chat or /flights etc.)
  const handleOverviewClick = (e: React.MouseEvent) => {
    e.preventDefault();

    // On Market Demand the current URL is the truth, not the saved context —
    // the saved context is that same Market Demand path, so restoring it would
    // leave the user exactly where they are.
    if (location.pathname.startsWith("/market-demand")) {
      navigate(toOverviewPath(location.pathname));
      return;
    }

    try {
      const raw = sessionStorage.getItem("ai.lastContext");
      if (raw) {
        const ctx = JSON.parse(raw) as { current_page?: string };
        const page = ctx?.current_page ?? "/";
        // Only restore retail paths: "/", "/store/...", "/market-demand/..."
        // Do not restore /chat, /flights, /plays etc. — fall back to "/" for those.
        const isRetailPath =
          page === "/" ||
          page.startsWith("/store/") ||
          page.startsWith("/market-demand");
        navigate(isRetailPath ? toOverviewPath(page) : "/");
        return;
      }
    } catch {
      // sessionStorage unavailable or invalid JSON — fall through
    }
    navigate("/");
  };

  const isOverviewActive =
    location.pathname === "/" || location.pathname.startsWith("/store/");

  const otherItems: ItemProps[] = [
    ...(marketDemandEnabled
      ? [
          {
            url: currentStore
              ? `/market-demand/store/${currentStore}`
              : `/market-demand`,
            title: "Market Demand",
          },
        ]
      : []),
    {
      url: `/forecast`,
      title: "Forecast",
    },
  ];

  return (
    <>
      {/* Overview rendered standalone so it can use a smart onClick */}
      <div className="pl-0 pr-3 py-2 mb-2">
        <div className="mb-1.5 text-xs font-bold uppercase tracking-wider text-indigo-300">
          RETAIL
        </div>
        <div className="h-0.5 w-8 rounded-full bg-indigo-500/70"></div>
      </div>
      <SidebarMenu className="px-0 mb-0">
        <SidebarMenuItem>
          <SidebarMenuButton
            isActive={isOverviewActive}
            onClick={handleOverviewClick}
            className="cursor-pointer"
          >
            <span className="break-words hyphens-auto leading-normal whitespace-normal">
              Overview
            </span>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
      {/* Remaining items rendered normally */}
      {otherItems.length > 0 && (
        <GroupMenu title="" items={otherItems} defaultOpen={true} />
      )}
    </>
  );
}

function RetailConfigurationSection() {
  const { is_admin } = useSessionData();

  if (!is_admin) {
    return null;
  }
  const items: ItemProps[] = [
    {
      url: `/setup/rules`,
      title: "Manage Plays",
    },
  ];
  return <GroupMenu title={"Configuration"} items={items} defaultOpen={true} />;
}

function RetailExploreSection() {
  const { retailAiChatEnabled, retailFlightsEnabled } = useAirport();

  const items: ItemProps[] = [];

  if (retailAiChatEnabled) {
    items.push({
      url: `/chat`,
      title: "Intelligence",
    });
  }

  if (retailFlightsEnabled) {
    items.push({
      url: `/flights`,
      title: "Flights",
    });
  }

  items.push({
    url: `/plays`,
    title: "Plays",
  });

  return <GroupMenu title={"Data"} items={items} defaultOpen={true} />;
}

export function RetailAppSidebar() {
  return (
    <Sidebar>
      <SidebarHeader>
        <Link to={"/"}>
          <div className="mt-2 flex items-center cursor-pointer">
            <div className="text-2xl font-bold">RaidoHub</div>
            <span
              className={
                "ml-2 mr-2 rounded border border-[#223381]/80 bg-[#223381] px-2 py-1 text-center text-xs font-semibold tracking-wide text-white shadow-sm shadow-[#223381]/40"
              }
            >
              AI
            </span>
          </div>
        </Link>
      </SidebarHeader>
      <SidebarContent>
        <RetailOverviewSection />
        <RetailExploreSection />
        <RetailConfigurationSection />
      </SidebarContent>
    </Sidebar>
  );
}
